import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getNotificationsByRole,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from '@/services/notifications';
import { useSiteSettings } from '@/settings/useSiteSettings';
import type { Notification } from '@/domain/types';

interface NotificationDropdownProps {
  mode: 'customer' | 'admin';
}

const iconMap: Record<string, string> = {
  order: 'ri-shopping-bag-3-line',
  license: 'ri-key-2-line',
  webhook: 'ri-bug-line',
  product: 'ri-box-3-line',
  site: 'ri-global-line',
};

const colorMap: Record<string, string> = {
  order: 'bg-sky-50 text-sky-600',
  license: 'bg-violet-50 text-violet-600',
  webhook: 'bg-red-50 text-red-600',
  product: 'bg-brand-50 text-brand-600',
  site: 'bg-amber-50 text-amber-600',
};

export default function NotificationDropdown({
  mode,
}: NotificationDropdownProps) {
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function loadNotifications() {
      setLoading(true);
      const data = await getNotificationsByRole(mode);
      setItems(data);
      setLoading(false);
    }
    loadNotifications();
  }, [mode]);

  const enabled =
    mode === 'admin'
      ? settings.notifications.adminEnabled
      : settings.notifications.customerEnabled;

  const visibleItems = items.filter((notif) => {
    if (mode === 'admin' && !notif.link.startsWith('/admin')) return false;
    if (mode === 'customer' && !notif.link.startsWith('/dashboard'))
      return false;
    if (notif.type === 'order') return settings.notifications.newOrder;
    if (notif.type === 'license' || notif.type === 'site')
      return settings.notifications.licenseActivation;
    if (notif.type === 'webhook') return settings.notifications.webhookFailure;
    return true;
  });

  const unreadCount = visibleItems.filter((n) => !n.read).length;

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [open]);

  async function markRead(id: string) {
    const success = await markNotificationAsRead(id);
    if (success) {
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
      );
    }
  }

  async function markAllRead() {
    const success = await markAllNotificationsAsRead();
    if (success) {
      const visibleIds = new Set(visibleItems.map((n) => n.id));
      setItems((prev) =>
        prev.map((n) => (visibleIds.has(n.id) ? { ...n, read: true } : n)),
      );
    }
  }

  function handleClickNotif(notif: Notification) {
    markRead(notif.id);
    setOpen(false);
    if (mode === 'admin' && notif.link.startsWith('/admin')) {
      navigate(notif.link);
    } else if (mode === 'customer' && notif.link.startsWith('/dashboard')) {
      navigate(notif.link);
    }
  }

  if (!enabled) {
    return null;
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((s) => !s)}
        className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500 relative">
        <i className="ri-notification-3-line" />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl border border-surface-200 shadow-lg z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-surface-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-surface-900">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs font-medium text-brand-600 hover:text-brand-700">
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {visibleItems.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-surface-400">
                No notifications yet
              </div>
            ) : (
              visibleItems.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleClickNotif(notif)}
                  className={`flex items-start gap-3 px-4 py-3 hover:bg-surface-50 cursor-pointer border-b border-surface-50 last:border-b-0 transition-colors ${
                    notif.read ? 'opacity-70' : ''
                  }`}>
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      colorMap[notif.type] || 'bg-surface-100 text-surface-500'
                    }`}>
                    <i
                      className={`${iconMap[notif.type] || 'ri-bell-line'} text-sm`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-surface-900 truncate">
                        {notif.title}
                      </p>
                      {!notif.read && (
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-surface-500 mt-0.5 line-clamp-2">
                      {notif.message}
                    </p>
                    <p className="text-[11px] text-surface-400 mt-1">
                      {notif.createdAt}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="px-4 py-2.5 border-t border-surface-100 bg-surface-50/50">
            <button
              onClick={() => {
                setOpen(false);
                navigate(mode === 'admin' ? '/admin' : '/dashboard');
              }}
              className="w-full text-center text-xs font-medium text-surface-500 hover:text-surface-700">
              View activity feed
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
